"use client";

import { useEffect, useRef } from "react";
import styles from "./location.module.css";

// Dotted globe centred on a city. It sways gently so the pin never goes behind the globe.
//   lat, lon: the city's coordinates (degrees)
//   tilt:     how far the globe leans toward the viewer; pick a value near the city's latitude
const R = 170;
const C = 230;
const D = Math.PI / 180;
const SWAY_DEG = 28;
const SWAY_MS = 14000;

function makeProjector(tiltDeg) {
  const sinT = Math.sin(tiltDeg * D);
  const cosT = Math.cos(tiltDeg * D);
  return (latDeg, dLon) => {
    const la = latDeg * D;
    const sl = Math.sin(la);
    const cl = Math.cos(la);
    const cd = Math.cos(dLon);
    return {
      c: sinT * sl + cosT * cl * cd,
      x: C + R * cl * Math.sin(dLon),
      y: C - R * (cosT * sl - sinT * cl * cd),
    };
  };
}

function dotAttrs(q) {
  if (q.c <= 0) return { cx: "0", cy: "0", r: "0", opacity: "0" };
  return {
    cx: q.x.toFixed(1),
    cy: q.y.toFixed(1),
    r: (1 + 1.4 * q.c).toFixed(2),
    opacity: (0.08 + 0.55 * Math.pow(q.c, 1.5)).toFixed(2),
  };
}

const DOTS = (() => {
  const out = [];
  for (let row = 0; row <= 20; row++) {
    const lat = -75 + row * 7.5;
    const count = Math.max(6, Math.round(48 * Math.cos(lat * D)));
    for (let i = 0; i < count; i++) out.push({ lat, lon: -180 + (i * 360) / count });
  }
  return out;
})();

export default function HeroGlobe({ lat, lon, tilt = 10 }) {
  const svgRef = useRef(null);
  const pinRef = useRef(null);
  const project = makeProjector(tilt);
  const startPin = project(lat, 0);

  useEffect(() => {
    const svg = svgRef.current;
    const pin = pinRef.current;
    if (!svg || !pin) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const proj = makeProjector(tilt);
    const circles = Array.from(svg.querySelectorAll("[data-dot]"));
    let frameId = 0;
    let last = 0;
    let visible = true;

    const draw = (lon0) => {
      for (let i = 0; i < circles.length; i++) {
        const attrs = dotAttrs(proj(DOTS[i].lat, (DOTS[i].lon - lon0) * D));
        const el = circles[i];
        if (attrs.r === "0" && el.getAttribute("r") === "0") continue;
        el.setAttribute("cx", attrs.cx);
        el.setAttribute("cy", attrs.cy);
        el.setAttribute("r", attrs.r);
        el.setAttribute("opacity", attrs.opacity);
      }
      const p = proj(lat, (lon - lon0) * D);
      pin.setAttribute("transform", `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})`);
    };

    const frame = (t) => {
      if (!visible) {
        frameId = 0;
        return;
      }
      frameId = requestAnimationFrame(frame);
      if (t - last < 33) return; // ~30fps is plenty for a slow sway
      last = t;
      draw(lon + SWAY_DEG * Math.sin((t / SWAY_MS) * 2 * Math.PI));
    };

    const start = () => {
      if (!frameId) frameId = requestAnimationFrame(frame);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
    });
    observer.observe(svg);
    start();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, [lat, lon, tilt]);

  return (
    <div className={styles.heroArt} aria-hidden="true">
      <svg ref={svgRef} viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" role="presentation">
        <defs>
          <radialGradient id="gGlow" cx="50%" cy="50%" r="50%">
            <stop offset="55%" stopColor="#0047AB" stopOpacity=".45" />
            <stop offset="75%" stopColor="#420EA9" stopOpacity=".18" />
            <stop offset="100%" stopColor="#080400" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="gBody" cx="40%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#00145B" />
            <stop offset="100%" stopColor="#080400" />
          </radialGradient>
          <radialGradient id="gPinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#CEFF1A" stopOpacity=".45" />
            <stop offset="100%" stopColor="#CEFF1A" stopOpacity="0" />
          </radialGradient>
          <filter id="gRimBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* outer glow */}
        <circle cx="230" cy="230" r="228" fill="url(#gGlow)" />

        {/* orbit ring, back half */}
        <g transform="rotate(-18 230 230)">
          <path d="M18 230 A212 58 0 0 1 442 230" fill="none" stroke="rgba(206,255,26,.30)" strokeWidth="1.2" strokeDasharray="2 7" />
        </g>

        {/* globe */}
        <circle cx="230" cy="230" r="176" fill="none" stroke="#0047AB" strokeOpacity=".55" strokeWidth="10" filter="url(#gRimBlur)" />
        <circle cx="230" cy="230" r="170" fill="url(#gBody)" stroke="#0047AB" strokeOpacity=".7" strokeWidth="1.5" />
        <g fill="#FBFDFD">
          {DOTS.map((dot, i) => (
            <circle key={i} data-dot="" suppressHydrationWarning {...dotAttrs(project(dot.lat, (dot.lon - lon) * D))} />
          ))}
        </g>

        {/* city pin */}
        <g ref={pinRef} transform={`translate(${startPin.x.toFixed(1)} ${startPin.y.toFixed(1)})`} suppressHydrationWarning>
          <circle r="34" cy="-14" fill="url(#gPinGlow)" />
          <ellipse className={styles.pinPulse} rx="10" ry="4" fill="none" stroke="#CEFF1A" strokeWidth="1.6" />
          <path d="M0 0 C-4 -10 -13 -15 -13 -25 A13 13 0 1 1 13 -25 C13 -15 4 -10 0 0Z" fill="#CEFF1A" />
          <circle cy="-25" r="5" fill="#080400" />
        </g>

        {/* orbit ring, front half, with satellite */}
        <g transform="rotate(-18 230 230)">
          <path d="M442 230 A212 58 0 0 1 18 230" fill="none" stroke="rgba(206,255,26,.55)" strokeWidth="1.2" strokeDasharray="2 7" />
          <circle r="4" fill="#CEFF1A">
            <animateMotion dur="16s" repeatCount="indefinite" path="M442 230 A212 58 0 0 1 18 230 A212 58 0 0 1 442 230" />
          </circle>
        </g>

        {/* shooting star, an echo of the leadworkz.com hero */}
        <path className={styles.starTrail} d="M24 150 Q150 20 440 58" fill="none" stroke="#CEFF1A" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </div>
  );
}
